import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/feature/LanguageSelector';

export default function Layout({ children }: { children: React.ReactNode }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isTelecomOpen, setIsTelecomOpen] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [newsletterError, setNewsletterError] = useState('');
  const location = useLocation();
  const { t } = useTranslation();

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/solutions', label: t('nav.solutions') },
    { to: '/projets', label: t('nav.projets') },
    { to: '/a-propos', label: t('nav.aPropos') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const telecomLinks = [
    { to: '/telecom-infrastructures', label: t('nav.telecomInfrastructures') || 'Infrastructures' },
    { to: '/telecom-fibre', label: t('nav.telecomFibre') || 'Fibre Optique' },
    { to: '/telecom-maintenance', label: t('nav.telecomMaintenance') || 'Maintenance' },
  ];

  const btpLinks = [
    { to: '/genie-civil-btp', label: t('nav.genieCivilBTP') || 'Génie Civil & BTP' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMenuOpen(false);
    setIsTelecomOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const isTelecomActive = telecomLinks.some((l) => isActive(l.to));

  const isBTPActive = btpLinks.some((l) => isActive(l.to));

  const handleNewsletterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    setNewsletterError('');

    const form = e.currentTarget;
    const formDataObj = new FormData(form);
    const hpValue = (formDataObj.get('website_alt') as string || '').trim();
    if (hpValue !== '') { setNewsletterStatus('success'); setTimeout(() => setNewsletterStatus('idle'), 3000); return; }

    const payload = new URLSearchParams();
    payload.append('email', newsletterEmail);
    payload.append('formId', 'd9q7v6fnahb7382ov7a0');

    fetch('https://readdy.ai/api/form/d9q7v6fnahb7382ov7a0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString(),
    })
      .then(async (response) => {
        const responseText = await response.text();
        let parsed; try { parsed = JSON.parse(responseText); } catch { /* noop */ }
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (!response.ok || parsed?.code !== 'OK' || (serverMsg && serverMsg.toLowerCase().includes('spam'))) {
          setNewsletterError(serverMsg || 'Une erreur est survenue.');
          setNewsletterStatus('error');
          return;
        }
        setNewsletterStatus('success');
        setNewsletterEmail('');
        setTimeout(() => setNewsletterStatus('idle'), 5000);
      })
      .catch(() => { setNewsletterError('Une erreur est survenue lors de l\'inscription.'); setNewsletterStatus('error'); });
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center cursor-pointer shrink-0">
              <img
                src="https://static.readdy.ai/image/91fcefbb4717a40f72927278ebfb44c9/b21d4969bbd2d15083f7f8fa7d846874.png"
                alt="IS TELECOM"
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              <Link
                to="/"
                className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-full ${
                  isActive('/')
                    ? 'text-foreground-950'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {t('nav.home')}
                {isActive('/') && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500"></span>
                )}
              </Link>

              {/* Telecom Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setIsTelecomOpen(true)}
                onMouseLeave={() => setIsTelecomOpen(false)}
              >
                <button
                  className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-full flex items-center gap-1 ${
                    isTelecomActive
                      ? 'text-foreground-950'
                      : 'text-foreground-700 hover:text-foreground-950'
                  }`}
                >
                  {t('nav.telecom') || 'Télécoms'}
                  <i className="ri-arrow-down-s-line w-4 h-4 flex items-center justify-center"></i>
                  {isTelecomActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500"></span>
                  )}
                </button>
                {/* invisible bridge to prevent gap between button and dropdown */}
                {isTelecomOpen && <div className="absolute top-full left-0 right-0 h-3" />}
                {isTelecomOpen && (
                  <div className="absolute top-[calc(100%+12px)] left-0 bg-white rounded-2xl shadow-xl border border-background-200/70 py-2 min-w-[220px] overflow-hidden">
                    {telecomLinks.map((link) => (
                      <Link
                        key={link.to}
                        to={link.to}
                        className={`block px-5 py-3 text-sm font-medium transition-colors cursor-pointer ${
                          isActive(link.to)
                            ? 'text-primary-600 bg-primary-50'
                            : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    ))}
                    <div className="border-t border-background-200/70 my-1" />
                    <Link
                      to="/solutions"
                      className="block px-5 py-3 text-sm font-medium text-foreground-500 hover:text-foreground-950 hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      {t('nav.solutions')}
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/genie-civil-btp"
                className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-full ${
                  isBTPActive
                    ? 'text-foreground-950'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {t('nav.genieCivilBTP') || 'Génie Civil & BTP'}
                {isBTPActive && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-secondary-500"></span>
                )}
              </Link>

              <Link
                to="/projets"
                className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-full ${
                  isActive('/projets')
                    ? 'text-foreground-950'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {t('nav.projets')}
                {isActive('/projets') && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500"></span>
                )}
              </Link>
              <Link
                to="/a-propos"
                className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-full ${
                  isActive('/a-propos')
                    ? 'text-foreground-950'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {t('nav.aPropos')}
                {isActive('/a-propos') && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500"></span>
                )}
              </Link>
              <Link
                to="/contact"
                className={`relative px-4 py-2 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap rounded-full ${
                  isActive('/contact')
                    ? 'text-foreground-950'
                    : 'text-foreground-700 hover:text-foreground-950'
                }`}
              >
                {t('nav.contact')}
                {isActive('/contact') && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary-500"></span>
                )}
              </Link>

              {/* Language Selector */}
              <div className="ml-2">
                <LanguageSelector />
              </div>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link
                to="/devis"
                className="inline-flex items-center gap-2 bg-background-950 text-white hover:bg-background-800 px-5 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                {t('nav.getQuote')}
                <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden flex items-center gap-2">
              <LanguageSelector />
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-foreground-950 hover:text-primary-500 p-2 cursor-pointer"
                aria-label="Menu"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-background-200 shadow-lg">
            <div className="px-4 pt-4 pb-6 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-4 py-3 font-medium cursor-pointer rounded-xl ${
                    isActive(link.to)
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {/* Mobile Telecom Submenu */}
              <div className="px-4 py-2">
                <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Télécoms</div>
                <div className="space-y-1">
                  {telecomLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block px-4 py-2 text-sm font-medium cursor-pointer rounded-xl ${
                        isActive(link.to)
                          ? 'text-primary-500 bg-primary-50'
                          : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="px-4 py-2">
                <div className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Génie Civil & BTP</div>
                <div className="space-y-1">
                  {btpLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`block px-4 py-2 text-sm font-medium cursor-pointer rounded-xl ${
                        isActive(link.to)
                          ? 'text-primary-500 bg-primary-50'
                          : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="pt-3 px-4">
                <Link
                  to="/devis"
                  onClick={() => setIsMenuOpen(false)}
                  className="inline-flex items-center gap-2 bg-background-950 text-white hover:bg-background-800 px-5 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap w-full justify-center"
                >
                  {t('nav.getQuote')}
                  <i className="ri-arrow-right-line text-sm w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="relative bg-background-950 text-white overflow-hidden">
        {/* Background image overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=Telecommunications%20tower%20silhouettes%20against%20dramatic%20sunset%20sky%20in%20Africa%2C%20multiple%20cellular%20towers%20with%20antennas%20creating%20industrial%20skyline%2C%20cinematic%20photography%20with%20warm%20golden%20and%20dark%20tones%2C%20telecom%20infrastructure%20landscape%20at%20dusk&width=1920&height=800&seq=footer-tel-bg-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-background-950/80"></div>

        <div className="relative z-10">
          {/* CTA Section */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 border-b border-white/10">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div>
                <h2 className="text-3xl md:text-5xl font-bold font-heading leading-tight">
                  <span className="text-white">{t('footer.ctaTitle1')}</span>
                  <br />
                  <span className="text-accent-400">{t('footer.ctaTitle2')}</span>
                  <br />
                  <span className="text-white">{t('footer.ctaTitle3')}</span>
                </h2>
              </div>
              <Link
                to="/devis"
                className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-950 px-8 py-4 rounded-full text-lg font-semibold transition-all cursor-pointer whitespace-nowrap shrink-0"
              >
                {t('footer.ctaButton')}
                <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
              </Link>
            </div>
          </div>

          {/* Footer Content */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="grid lg:grid-cols-12 gap-12">
              {/* Company Info */}
              <div className="lg:col-span-4">
                <Link to="/" className="inline-block mb-6">
                  <img
                    src="https://static.readdy.ai/image/91fcefbb4717a40f72927278ebfb44c9/b21d4969bbd2d15083f7f8fa7d846874.png"
                    alt="IS TELECOM"
                    className="h-14 w-auto brightness-0 invert"
                  />
                </Link>
                <p className="text-background-300 text-sm leading-relaxed mb-6 max-w-sm">
                  {t('footer.tagline')}
                </p>
                <form className="flex gap-2" onSubmit={handleNewsletterSubmit} data-readdy-form="">
                  <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                  <input
                    type="email"
                    name="email"
                    placeholder={t('footer.subscribePlaceholder')}
                    className="flex-1 px-4 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-background-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={newsletterEmail}
                    onChange={(e) => { setNewsletterEmail(e.target.value); setNewsletterStatus('idle'); }}
                    required
                  />
                  <button
                    type="submit"
                    disabled={newsletterStatus === 'submitting'}
                    className="px-6 py-3 rounded-full bg-accent-500 hover:bg-accent-600 disabled:bg-accent-400 text-background-950 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                  >
                    {newsletterStatus === 'submitting' ? (
                      <span className="flex items-center gap-1"><i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i></span>
                    ) : (
                      t('footer.subscribe')
                    )}
                  </button>
                </form>
                {newsletterStatus === 'success' && (
                  <p className="text-accent-400 text-xs mt-2 flex items-center gap-1"><i className="ri-check-line w-4 h-4 flex items-center justify-center"></i>{t('footer.subscribeSuccess') || 'Inscription réussie !'}</p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="text-red-400 text-xs mt-2">{newsletterError}</p>
                )}
              </div>

              {/* Telecom Services */}
              <div className="lg:col-span-2">
                <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">Télécoms</h4>
                <div className="space-y-3">
                  {[
                    { to: '/telecom-infrastructures', label: t('footer.telecomList.towers') || 'Tours & Pylônes' },
                    { to: '/telecom-fibre', label: t('footer.telecomList.fibre') || 'Fibre Optique' },
                    { to: '/telecom-maintenance', label: t('footer.telecomList.maintenance') || 'Maintenance NOC' },
                    { to: '/genie-civil-btp', label: t('nav.genieCivilBTP') || 'Génie Civil & BTP' },
                  ].map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="block text-background-300 hover:text-accent-400 transition-colors text-sm cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Services */}
              <div className="lg:col-span-2 lg:col-start-6">
                <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">{t('footer.services')}</h4>
                <div className="space-y-3">
                  {[
                    { to: '/autres-services', label: t('footer.servicesList.telecom') },
                    { to: '/solutions', label: t('footer.servicesList.microgrids') },
                    { to: '/solutions', label: t('footer.servicesList.solarPumping') },
                    { to: '/solutions', label: t('footer.servicesList.storage') },
                    { to: '/solutions', label: t('footer.servicesList.evCharging') },
                    { to: '/autres-services', label: t('footer.servicesList.constructionMetal') },
                    { to: '/autres-services', label: t('footer.servicesList.itNetworks') },
                    { to: '/autres-services', label: t('footer.servicesList.audit') },
                    { to: '/autres-services', label: t('footer.servicesList.transport') },
                    { to: '/autres-services', label: t('footer.servicesList.expertise') },
                  ].map((link) => (
                    <Link
                      key={link.label}
                      to={link.to}
                      className="block text-background-300 hover:text-accent-400 transition-colors text-sm cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Company */}
              <div className="lg:col-span-2">
                <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">{t('footer.company')}</h4>
                <div className="space-y-3">
                  {[
                    { to: '/a-propos', label: t('footer.companyList.about') },
                    { to: '/projets', label: t('footer.companyList.projects') },
                    { to: '/produits', label: t('footer.companyList.products') },
                    { to: '/contact', label: t('footer.companyList.contact') },
                  ].map((link) => (
                    <Link
                      key={link.label}
                      to={link.to}
                      className="block text-background-300 hover:text-accent-400 transition-colors text-sm cursor-pointer"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div className="lg:col-span-2">
                <h4 className="font-semibold text-white mb-5 text-sm uppercase tracking-wider">{t('footer.contact')}</h4>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-phone-line text-accent-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <a href="tel:+2290195892755" className="text-background-200 hover:text-accent-400 transition-colors text-sm cursor-pointer">+229 01 95 89 27 55</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-whatsapp-line text-accent-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <a href="https://wa.me/33758329313" target="_blank" rel="noopener noreferrer" className="text-background-200 hover:text-accent-400 transition-colors text-sm cursor-pointer">WhatsApp : +33 7 58 32 93 13</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-mail-line text-accent-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <a href="mailto:contact@is-telecom.co" className="text-background-200 hover:text-accent-400 transition-colors text-sm cursor-pointer">contact@is-telecom.co</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-accent-400 text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="text-background-200 text-sm">Cotonou, Bénin</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Copyright bar */}
          <div className="border-t border-white/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-background-400 text-sm">
                  {t('footer.copyright')}
                </p>
                <div className="flex items-center gap-4 flex-wrap justify-center">
                  <Link to="/conditions-generales" className="text-background-400 hover:text-accent-400 transition-colors text-sm cursor-pointer">{t('footer.terms')}</Link>
                  <Link to="/politique-confidentialite" className="text-background-400 hover:text-accent-400 transition-colors text-sm cursor-pointer">{t('footer.privacy')}</Link>
                  <a href="#" className="text-background-400 hover:text-accent-400 transition-colors cursor-pointer" aria-label="Facebook">
                    <i className="ri-facebook-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <a href="#" className="text-background-400 hover:text-accent-400 transition-colors cursor-pointer" aria-label="LinkedIn">
                    <i className="ri-linkedin-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <a href="#" className="text-background-400 hover:text-accent-400 transition-colors cursor-pointer" aria-label="Twitter">
                    <i className="ri-twitter-x-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                  <a href="#" className="text-background-400 hover:text-accent-400 transition-colors cursor-pointer" aria-label="Instagram">
                    <i className="ri-instagram-line text-lg w-5 h-5 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}